import { Badge, Text } from "@mantine/core";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";

import { modals } from "@mantine/modals";
import { repositoryMdm } from "../../../../_base/_const/_constVar";
import { formatDateTransfer } from "../../../../common/FormatDate/FormatDate";
import { _localization } from "../../../../config/location";

interface Vendor {
  id: number;
  orgId: number | null;
  vendorName: string;
  vendorCode: string;
  enabledFlag: string;
  startDateActive: string; // ISO date string
  endDateActive: string | null; // ISO date string or null
  vendorTypeLookupCode: string | null;
  employeeId: number | null;
  email: string | null;
  phone: string | null;
  addr: string;
  creditLimit: number | null;
  vatRegistrationNum: string;
  createdBy: number | null;
  creationDate: string | null; // ISO date string or null
}

const ListSupplier = ({
  handleListSupplier,
  params,
}: {
  handleListSupplier: Function;
  navigate?: any;
  params?: any;
  isChooseCongNo?: boolean;
  disible?: boolean;
}) => {
  //data and fetching state
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<Vendor[]>([]);
  const [allData, setAllData] = useState<Vendor[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  //table state
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  //count

  const columns = React.useMemo<MRT_ColumnDef<Vendor>[]>(
    () => [
      {
        accessorKey: "index",
        header: "STT",
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Text size="12.5px">{row.index === -1 ? "" : row.index + 1}</Text>
        ),
        size: 50,
      },
      {
        accessorKey: "vendorCode",
        enableColumnActions: false,
        header: "Mã nhóm công nợ",
        Cell: ({ renderedCellValue, row }) => (
          <Badge
            radius="sm"
            variant="light"
            color="blue"
            style={{ cursor: "pointer" }}
          >
            {renderedCellValue === null ? null : renderedCellValue}
          </Badge>
        ),
        size: 250,
      },
      {
        accessorKey: "vendorName",
        enableColumnActions: false,
        header: "Tên nhóm công nợ",
        Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
        size: 400,
      },
      {
        accessorKey: "startDateActive",
        enableColumnActions: false,
        header: "Ngày bắt đầu",
        Cell: ({ renderedCellValue }) => (
          <>
            {renderedCellValue &&
              formatDateTransfer(renderedCellValue as string)}
          </>
        ),
      },
      {
        accessorKey: "addr",
        enableColumnActions: false,
        header: "Địa điểm",
        Cell: ({ renderedCellValue }) => (
          <>{renderedCellValue && renderedCellValue}</>
        ),
        size: 700,
      },
      {
        accessorKey: "phone",
        enableColumnActions: false,
        header: "Số điện thoại",
        Cell: ({ renderedCellValue, row }: any) => {
          return <>{renderedCellValue === null ? null : renderedCellValue}</>;
        },
      },
    ],
    []
  );
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await repositoryMdm.get(`/api/v1/TblVendor/get-all`);
      if (response && response.success) {
        let result = response.data;
        setIsLoading(false);
        setData(result);
        setAllData(result);
        setSelectIds([]);
        table.resetRowSelection();
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      return;
    } finally {
      setIsRefetching(false);
    }
  };

  // const paginate = (allData: any[]) => {
  //   const start = pagination.pageIndex * pagination.pageSize;
  //   const end = start + pagination.pageSize;
  //   return allData.slice(start, end);
  // };

  // useEffect(() => {
  //   if (allData.length > 0) {
  //     setData(paginate(allData)); //
  //   }
  // }, [pagination.pageIndex, pagination.pageSize, allData]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (210 + headerHeight));
    };
    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  useEffect(() => {
    fetchData();
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: true,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    mantineBottomToolbarProps: {
      style: {
        display: "none",
      },
    },
    enableGlobalFilterModes: true,
    positionGlobalFilter: "left",
    localization: _localization,
    paginationDisplayMode: "pages",
    mantinePaginationProps: {
      withEdges: false,
      showRowsPerPage: false,
    },
    getRowId: (row) => row.id?.toString(),
    initialState: {
      columnPinning: {
        left: ["index", "vendorCode"],
        right: ["action"],
      },
      columnVisibility: { id: true },
      density: "xs",
      showGlobalFilter: true,
    },
    mantineSearchTextInputProps: {
      placeholder: `Vui lòng tìm kiếm`,
      variant: "filled",
    },
    enablePagination: true,
    enableRowVirtualization: true,
    enableRowSelection: false,
    mantineTableContainerProps: {
      style: { maxHeight: height - 100, minHeight: height - 100 },
    },
    enableStickyHeader: true,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading,
    },
    rowCount: allData.length,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: true,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "550",
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),

    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => {
        handleListSupplier(row.original, params);
        modals.closeAll();
      },
      sx: {
        cursor: "pointer", //you might want to change the cursor too when adding an onClick
      },
    }),
    mantineToolbarAlertBannerProps: isLoading
      ? { color: "blue", children: "Đang tải dữ liệu..." }
      : undefined,
  });

  return (
    <>
      <div
        style={{ border: "1px solid #dee2e6", padding: "5px" }}
        ref={headerRef}
      ></div>
      <div className="mt-5" style={{ marginTop: "10px" }}>
        <MantineReactTable table={table} />
      </div>
    </>
  );
};

export default ListSupplier;
